/* DieRych — launch page behaviour.
   Progressive enhancement only: every section renders complete without it. */
import { CartAddEvent } from '@theme/events';

const root = document.documentElement;
root.classList.add('dl-js');

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Reveal: statement highlights and image clip-ins */
function initReveal(scope = document) {
  const targets = scope.querySelectorAll('[data-dl-reveal]:not([data-dl-bound])');
  if (!targets.length) return;

  targets.forEach((el) => {
    el.setAttribute('data-dl-bound', '');
    if (el.matches('[data-dl-reveal="statement"]')) {
      el.querySelectorAll('.dl-statement__text strong').forEach((strong, i) => {
        strong.style.setProperty('--dl-i', String(i));
      });
    }
  });

  if (reduceMotion.matches || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-inview'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          io.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -18% 0px', threshold: 0.12 }
  );
  targets.forEach((el) => io.observe(el));
}

/* Colours viewer */
function initColours(scope = document) {
  scope.querySelectorAll('[data-dl-colours]:not([data-dl-bound])').forEach((section) => {
    section.setAttribute('data-dl-bound', '');
    const buttons = [...section.querySelectorAll('[data-dl-colour]')];
    const slides = [...section.querySelectorAll('[data-dl-colour-slide]')];
    const infos = [...section.querySelectorAll('[data-dl-colour-info]')];

    const select = (index) => {
      buttons.forEach((b) => {
        const on = b.dataset.dlColour === index;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', String(on));
      });
      slides.forEach((s) => {
        const on = s.dataset.dlColourSlide === index;
        s.classList.toggle('is-active', on);
        if (on) {
          s.removeAttribute('aria-hidden');
          const img = s.querySelector('img');
          if (img && img.loading === 'lazy') img.loading = 'eager';
        } else {
          s.setAttribute('aria-hidden', 'true');
        }
      });
      infos.forEach((i) => {
        const on = i.dataset.dlColourInfo === index;
        i.classList.toggle('is-active', on);
        i.hidden = !on;
      });
    };

    buttons.forEach((b) => b.addEventListener('click', () => select(b.dataset.dlColour)));

    // Warm the other images once the section is close to view
    if ('IntersectionObserver' in window) {
      const warm = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            slides.forEach((s) => {
              const img = s.querySelector('img');
              if (img) img.loading = 'eager';
            });
            warm.disconnect();
          }
        },
        { rootMargin: '400px 0px' }
      );
      warm.observe(section);
    }
  });
}

/* Launch viewer: one product film per colour, cycling until the visitor picks one */
function initViewer(scope = document) {
  scope.querySelectorAll('[data-dl-viewer]:not([data-dl-bound])').forEach((root) => {
    root.setAttribute('data-dl-bound', '');
    const slides = [...root.querySelectorAll('[data-dl-viewer-slide]')];
    const chips = [...root.querySelectorAll('[data-dl-viewer-chip]')];
    const nameEl = root.querySelector('.dl-viewer__finish-name');
    const hold = Number(root.dataset.hold) || 1400;
    const videoAt = (i) => slides[i]?.querySelector('video') || null;
    let current = 0;
    let auto = !reduceMotion.matches;
    let visible = false;
    let blocked = false; // autoplay refused (e.g. iOS Low Power Mode): cycle the stills instead
    let timer = null;

    const next = () => show((current + 1) % slides.length, true);

    const schedule = () => {
      clearTimeout(timer);
      if (auto && visible && slides.length > 1) timer = setTimeout(next, hold + 3200);
    };

    const play = (video) => {
      if (!video || reduceMotion.matches || !visible) return;
      if (blocked) return schedule();
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch((err) => {
          if (err && err.name === 'NotAllowedError') {
            blocked = true;
            schedule();
          }
        });
      }
    };

    const show = (index, fromStart) => {
      clearTimeout(timer);
      slides.forEach((slide, n) => {
        const on = n === index;
        slide.classList.toggle('is-active', on);
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        const v = slide.querySelector('video');
        if (v && !on) v.pause();
      });
      chips.forEach((chip, n) => {
        const on = n === index;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-pressed', String(on));
      });
      if (nameEl && chips[index]) nameEl.textContent = chips[index].dataset.label || '';
      current = index;
      const v = videoAt(index);
      if (v) {
        if (fromStart) {
          try { v.currentTime = 0; } catch (e) { /* not seekable yet */ }
        }
        play(v);
      } else {
        schedule();
      }
    };

    slides.forEach((slide, n) => {
      const v = slide.querySelector('video');
      if (!v) return;
      v.muted = true;
      v.playsInline = true;
      v.removeAttribute('controls');
      v.addEventListener('ended', () => {
        if (n !== current || !auto || slides.length < 2) return;
        timer = setTimeout(next, hold);
      });
    });

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        auto = false;
        show(Number(chip.dataset.dlViewerChip), true);
      });
    });

    const start = () => {
      const v = videoAt(current);
      if (!v) return show(current, false);
      if (v.ended) return auto ? next() : undefined;
      play(v);
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting;
            if (visible) start();
            else {
              clearTimeout(timer);
              videoAt(current)?.pause();
            }
          });
        },
        { threshold: 0.3 }
      ).observe(root);
    } else {
      visible = true;
      start();
    }

    // browsers pause silent video in background tabs; pick up again on return
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && visible) start();
    });

    // let the next colours buffer once the first is under way
    const first = videoAt(0);
    const warm = () => slides.forEach((slide) => {
      const v = slide.querySelector('video');
      if (v) v.preload = 'auto';
    });
    if (first) first.addEventListener('playing', warm, { once: true });
  });
}

/* Add to bag → Horizon cart drawer */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

function initAddToBag(scope = document) {
  scope.querySelectorAll('form[data-dl-add]:not([data-dl-bound])').forEach((form) => {
    form.setAttribute('data-dl-bound', '');
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('[data-dl-add-button]');
      const error = form.querySelector('.dl-card__error');
      if (!button || button.getAttribute('aria-busy') === 'true') return;

      button.setAttribute('aria-busy', 'true');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      const body = new FormData(form);
      const ids = cartSectionIds();
      if (ids.length) body.append('sections', ids.join(','));
      const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';

      try {
        const response = await fetch(`${url.replace(/\.js$/, '')}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'This item could not be added.');
        }

        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 2200);

        document.dispatchEvent(
          new CartAddEvent({}, String(body.get('id')), {
            source: 'dierych-launch',
            itemCount: Number(body.get('quantity')) || 1,
            productId: form.dataset.productId,
            sections: data.sections,
          })
        );
      } catch (err) {
        if (error) {
          error.textContent = err instanceof Error ? err.message : 'This item could not be added.';
          error.hidden = false;
        }
      } finally {
        button.removeAttribute('aria-busy');
      }
    });
  });
}

/* Sub-nav: mark the section in view, smooth anchor scrolling */
function initLocalNav() {
  const nav = document.querySelector('[data-dl-localnav]');
  if (!nav) return;
  const links = [...nav.querySelectorAll('.dl-localnav__link[href^="#"]')];
  const targets = links
    .map((a) => {
      const id = decodeURIComponent(a.getAttribute('href').slice(1));
      const el = id ? document.getElementById(id) : null;
      return el ? { a, el } : null;
    })
    .filter(Boolean);

  if (targets.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const match = targets.find((t) => t.el === entry.target);
          if (!match) return;
          if (entry.isIntersecting) {
            links.forEach((l) => l.removeAttribute('aria-current'));
            match.a.setAttribute('aria-current', 'true');
          }
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    targets.forEach((t) => io.observe(t.el));
  }

  const scrollTop = nav.querySelector('[data-dl-scrolltop]');
  scrollTop?.addEventListener('click', (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  });
}

function init(scope = document) {
  initReveal(scope);
  initColours(scope);
  initViewer(scope);
  initAddToBag(scope);
}

init();
initLocalNav();

/* Theme editor: re-bind when sections are re-rendered */
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target instanceof Element ? event.target : document;
  init(target);
});
